import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertTriangle, Tag, Calendar, Hash, Palette, ExternalLink, Copy, Bug } from 'lucide-react';
import { extractHexColors } from '@/utils/copilot/messageTypeDetector';

interface ActionResultMessageProps {
  content: string;
  status: 'success' | 'error' | 'warning';
  hasLinks?: boolean;
  colorHexes?: string[];
  structured_data?: any;
  onLinkClick?: (href: string) => void;
}

/**
 * Component for rendering action result messages with rich formatting
 */
export const ActionResultMessage: React.FC<ActionResultMessageProps> = ({ 
  content, 
  status, 
  hasLinks, 
  colorHexes,
  structured_data,
  onLinkClick 
}) => {
  // Extract color information
  const colors = colorHexes || extractHexColors(content);
  
  // Get status styling
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          icon: <CheckCircle className="h-5 w-5 text-green-600" />,
          bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
          borderColor: 'border-green-200',
          badgeColor: 'bg-green-100 text-green-800'
        };
      case 'error':
        return {
          icon: <XCircle className="h-5 w-5 text-red-600" />,
          bgColor: 'bg-gradient-to-br from-red-50 to-pink-50',
          borderColor: 'border-red-200',
          badgeColor: 'bg-red-100 text-red-800'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="h-5 w-5 text-yellow-600" />,
          bgColor: 'bg-gradient-to-br from-yellow-50 to-amber-50',
          borderColor: 'border-yellow-200',
          badgeColor: 'bg-yellow-100 text-yellow-800'
        };
    }
  };

  const statusConfig = getStatusConfig();

  // Custom link component
  const CustomLink = ({ href, children }: { href?: string; children: React.ReactNode }) => (
    <Button
      variant="link"
      size="sm"
      onClick={() => href && onLinkClick?.(href)}
      className="p-0 h-auto font-medium text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
    >
      {children}
      <ExternalLink className="h-3 w-3" />
    </Button>
  );

  // Parse content to extract structured data
  const parseStructuredData = () => {
    const data: { label: string; value: string; icon?: React.ReactNode }[] = [];
    const lines = content.split('\n');
    
    lines.forEach(line => {
      // Match patterns like "🏷️ **Name**: Priority,"
      const match = line.match(/^(.*?)\s*\*\*([^*]+)\*\*:\s*(.+)$/);
      if (match) {
        const [, emoji, label, value] = match;
        let icon: React.ReactNode = null;
        
        // Add appropriate icons based on label
        if (label.toLowerCase().includes('name')) icon = <Tag className="h-4 w-4" />;
        if (label.toLowerCase().includes('color')) icon = <Palette className="h-4 w-4" />;
        if (label.toLowerCase().includes('id')) icon = <Hash className="h-4 w-4" />;
        if (label.toLowerCase().includes('created')) icon = <Calendar className="h-4 w-4" />;
        
        data.push({
          label: label.trim(),
          value: value.trim().replace(/,$/, ''), // Remove trailing comma
          icon
        });
      }
    });
    
    return data;
  };

  const structuredData = parseStructuredData();

  // Handle structured data from backend (e.g., customer creation, company creation)
  const getStructuredEntityData = () => {
    if (!structured_data?.created_entity) return null;
    
    const entity = structured_data.created_entity;
    const entityType = structured_data.entity_type || 'entity';
    
    const entityData = [];
    
    // Company specific fields
    if (entityType === 'companie' || entityType === 'company') {
      if (entity.name) {
        entityData.push({
          label: 'Company Name',
          value: entity.name,
          icon: <Tag className="h-4 w-4" />
        });
      }
      
      if (entity.website) {
        entityData.push({
          label: 'Website',
          value: entity.website,
          icon: <ExternalLink className="h-4 w-4" />
        });
      }
      
      if (entity.email) {
        entityData.push({
          label: 'Email',
          value: entity.email,
          icon: <Hash className="h-4 w-4" />
        });
      }
      
      if (entity.phone) {
        entityData.push({
          label: 'Phone',
          value: entity.phone,
          icon: <Hash className="h-4 w-4" />
        });
      }
      
      if (entity.industry) {
        entityData.push({
          label: 'Industry',
          value: entity.industry,
          icon: <Tag className="h-4 w-4" />
        });
      }
      
      if (entity.numberOfEmployees) {
        entityData.push({
          label: 'Employees',
          value: entity.numberOfEmployees.toString(),
          icon: <Tag className="h-4 w-4" />
        });
      }
      
      if (entity.location || (entity.city && entity.state)) {
        const location = entity.location || `${entity.city || ''}, ${entity.state || ''}`.trim().replace(/^,|,$/, '');
        if (location) {
          entityData.push({
            label: 'Location',
            value: location,
            icon: <Tag className="h-4 w-4" />
          });
        }
      }
    }
    
    // Customer specific fields
    else if (entityType === 'customer' || entity.firstname || entity.lastname) {
      if (entity.firstname || entity.lastname) {
        entityData.push({
          label: 'Name',
          value: `${entity.firstname || ''} ${entity.lastname || ''}`.trim(),
          icon: <Tag className="h-4 w-4" />
        });
      }
      
      if (entity.email) {
        entityData.push({
          label: 'Email',
          value: entity.email,
          icon: <Hash className="h-4 w-4" />
        });
      }
      
      if (entity.phone) {
        entityData.push({
          label: 'Phone',
          value: entity.phone,
          icon: <Hash className="h-4 w-4" />
        });
      }
    }
    
    // Common fields for all entities
    if (entity.id) {
      entityData.push({
        label: 'ID',
        value: entity.id.substring(0, 8) + '...',
        icon: <Hash className="h-4 w-4" />
      });
    }
    
    if (entity.created_at) {
      entityData.push({
        label: 'Created',
        value: new Date(entity.created_at).toLocaleString(),
        icon: <Calendar className="h-4 w-4" />
      });
    }
    
    return {
      entityType,
      data: entityData,
      url: entity.url
    };
  };

  const entityData = getStructuredEntityData();

  // Check if this is a Linear issue creation
  const isLinearIssueCreation = structured_data?.response_type === 'created' && structured_data?.entity_type === 'linear_issue';
  const linearIssue = structured_data?.data;

  // Handle copy to clipboard functionality
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Render Linear issue creation success
  if (isLinearIssueCreation && status === 'success') {
    return (
      <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
        <div className="p-4">
          {/* Success Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">Linear Issue Created Successfully!</h3>
              <p className="text-sm text-gray-600">Your issue has been created and is ready for team collaboration</p>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1 bg-violet-100 rounded-lg text-sm font-medium text-violet-700">
              <Bug className="h-4 w-4" />
              <span>Linear</span>
            </div>
          </div>

          {/* Issue Details Card */}
          <div className="bg-white rounded-lg p-4 border border-violet-200 mb-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`
                  w-8 h-8 bg-gradient-to-br rounded-lg flex items-center justify-center
                  ${linearIssue?.category === 'Bug'
                    ? 'from-red-500 to-pink-600'
                    : linearIssue?.category === 'Feature'
                    ? 'from-violet-500 to-purple-600'
                    : linearIssue?.category === 'Task'
                    ? 'from-blue-500 to-indigo-600'
                    : 'from-gray-500 to-slate-600'
                  }
                `}>
                  <span className="text-white text-sm">
                    {linearIssue?.categoryIcon || '📝'}
                  </span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{linearIssue?.title}</h4>
                  {linearIssue?.issueNumber && (
                    <p className="text-sm text-violet-600 font-mono">{linearIssue.issueNumber}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Badge
                  variant="outline"
                  className={`
                    ${linearIssue?.category === 'Bug'
                      ? 'border-red-200 text-red-700 bg-red-50'
                      : linearIssue?.category === 'Feature'
                      ? 'border-violet-200 text-violet-700 bg-violet-50'
                      : linearIssue?.category === 'Task'
                      ? 'border-blue-200 text-blue-700 bg-blue-50'
                      : 'border-gray-200 text-gray-700 bg-gray-50'
                    }
                  `}
                >
                  {linearIssue?.category || 'General'}
                </Badge>
              </div>
            </div>

            {/* Issue Metadata */}
            <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-4">
              <div className="flex items-center gap-1">
                <Hash className="h-3 w-3" />
                <span>ID: {linearIssue?.id?.slice(0, 8)}...</span>
              </div>
              {linearIssue?.state && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                  <span>State: {linearIssue.state}</span>
                </div>
              )}
              {linearIssue?.workspace && (
                <div className="flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  <span>Workspace: {linearIssue.workspace}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
              {linearIssue?.url && (
                <Button
                  variant="default"
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={() => window.open(linearIssue.url, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View in Linear
                </Button>
              )}
              {linearIssue?.url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(linearIssue.url)}
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copy URL
                </Button>
              )}
              {linearIssue?.issueNumber && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(linearIssue.issueNumber)}
                >
                  <Hash className="h-3 w-3 mr-1" />
                  Copy ID
                </Button>
              )}
            </div>

            {/* Direct URL Link for easy access */}
            {linearIssue?.url && (
              <div className="mt-3 p-2 bg-gray-50 rounded border">
                <p className="text-xs text-gray-600 mb-1">Direct Link:</p>
                <a
                  href={linearIssue.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-violet-600 hover:text-violet-800 underline break-all"
                >
                  {linearIssue.url}
                </a>
              </div>
            )}
          </div>

          {/* Next Steps */}
          <div className="bg-violet-50 p-3 rounded-lg border border-violet-200">
            <h5 className="font-medium text-violet-900 mb-2">Next Steps</h5>
            <ul className="text-sm text-violet-700 space-y-1">
              <li>• Assign the issue to a team member</li>
              <li>• Add labels and set priority</li>
              <li>• Link related issues or PRs</li>
              <li>• Update the issue status as work progresses</li>
            </ul>
          </div>

          {/* Additional Content */}
          {content && content.trim() && (
            <div className="mt-4 text-sm text-gray-600">
              <ReactMarkdown
                components={{
                  a: ({ href, children }) => <CustomLink href={href}>{children}</CustomLink>
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card className={`p-4 ${statusConfig.bgColor} ${statusConfig.borderColor}`}>
      <div className="space-y-4">
        {/* Header with status */}
        <div className="flex items-start gap-3">
          {statusConfig.icon}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <ReactMarkdown
                components={{
                  p: ({ children }) => <h3 className="font-semibold text-gray-900 text-base m-0">{children}</h3>,
                  strong: ({ children }) => <span className="font-semibold">{children}</span>
                }}
              >
                {content.split('\n')[0]}
              </ReactMarkdown>
              <Badge className={statusConfig.badgeColor}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Badge>
            </div>
          </div>
        </div>

        {/* Entity Data from Backend (Priority Display) */}
        {entityData && (
          <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900 capitalize">
                {entityData.entityType} Details
              </h4>
              {entityData.url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onLinkClick?.(entityData.url)}
                  className="text-xs h-7"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {entityData.data.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  {item.icon}
                  <span className="text-sm font-medium text-gray-600 min-w-[50px]">
                    {item.label}:
                  </span>
                  <span className="text-sm text-gray-900 truncate">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Structured Data Display (from content parsing) */}
        {structuredData.length > 0 && (
          <div className="bg-white/60 rounded-lg p-3 space-y-2">
            {structuredData.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                {item.icon}
                <span className="text-sm font-medium text-gray-600 min-w-[60px]">
                  {item.label}:
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-900">{item.value}</span>
                  {/* Show color preview for color values */}
                  {item.label.toLowerCase().includes('color') && colors.length > 0 && (
                    <div
                      className="w-4 h-4 rounded border border-gray-300 shadow-sm"
                      style={{ backgroundColor: colors[0] }}
                      title={colors[0]}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Color Previews */}
        {colors.length > 0 && (
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-gray-500" />
            <span className="text-xs text-gray-600">Colors:</span>
            {colors.map((color, index) => (
              <div
                key={index}
                className="flex items-center gap-1 bg-white px-2 py-1 rounded text-xs border"
                title={`Color: ${color}`}
              >
                <div
                  className="w-3 h-3 rounded border border-gray-300"
                  style={{ backgroundColor: color }}
                />
                <code className="text-gray-600">{color}</code>
              </div>
            ))}
          </div>
        )}

        {/* Action Links */}
        {hasLinks && (
          <div className="pt-2 border-t border-gray-200">
            <ReactMarkdown
              components={{
                a: ({ href, children }) => <CustomLink href={href}>{children}</CustomLink>,
                p: ({ children }) => <p className="text-sm text-gray-700 m-0">{children}</p>,
                strong: ({ children }) => <strong className="font-medium">{children}</strong>
              }}
            >
              {content.split('\n').slice(-2).join('\n')}
            </ReactMarkdown>
          </div>
        )}

        {/* General content for non-structured parts */}
        <div className="text-sm text-gray-700">
          <ReactMarkdown
            components={{
              a: ({ href, children }) => <CustomLink href={href}>{children}</CustomLink>,
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              strong: ({ children }) => <strong className="font-medium">{children}</strong>
            }}
          >
            {content.split('\n').slice(1, -2).join('\n')}
          </ReactMarkdown>
        </div>
      </div>
    </Card>
  );
};